import React, { useState, useEffect } from "react";
import Loading from "../../Common/Loading";
import CustomAlert from '../../Common/CustomAlert';
import popup_bg1 from "../../Common/Images/ImagesForAlert/popup_bg1.png";
import alert_close_n from "../../Common/Images/ImagesForAlert/alert_close_n.png";
import alert_close_o from "../../Common/Images/ImagesForAlert/alert_close_o.png";

const API_BASE_URL =
  process.env.REACT_APP_DOCUMENT_LINK || "https://staging.sysadmin.elearnoncloud.com/";

interface Props {
  compId: number;
  onClose: () => void;
}

interface Winner {
  StudentName: string;
  GradeYear: string;
  SchoolCode: string;
  FamilyCode: string;
  Login: string;
  SchoolName: string;
  Address: string;
  Country: string;
  DisplayEmail: string;
  Status: number;
  Code: string;
  UniqueID: number;
  OriginalStatus?: number;
  OriginalCode?: string;
}

interface StatusOption {
  value: number;
  label: string;
}

const EL_CompWinnerDetails: React.FC<Props> = ({ compId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [winners, setWinners] = useState<Winner[]>([]);
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);
  const [compTitle, setCompTitle] = useState("");
  const [canSendVouchers, setCanSendVouchers] = useState(false);
  const [alertMsg, setAlertMsg] = useState('');

  useEffect(() => {
    if (compId) fetchDetails();
  }, [compId]);

  const fetchDetails = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}api/manage-comps/${compId}/winner_details/`
      );
      if (res.ok) {
        const data = await res.json();
        setCompTitle(data.comp_title);
        setStatusOptions(data.status_options);
        setCanSendVouchers(data.can_send_vouchers);

        const mappedWinners = data.winners.map((w: Winner) => ({
          ...w,
          OriginalStatus: w.Status,
          OriginalCode: w.Code || "",
        }));
        setWinners(mappedWinners);
      }
    } catch (e) {
      console.error(e);
      setAlertMsg("Network Error");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (idx: number, newVal: number) => {
    const updated = [...winners];
    updated[idx].Status = newVal;
    if (newVal !== 3) {
      updated[idx].Code = "";
    }
    setWinners(updated);
  };

  const handleCodeChange = (idx: number, newVal: string) => {
    const updated = [...winners];
    updated[idx].Code = newVal;
    setWinners(updated);
  };

  const handleSendVouchers = async () => {
    if (
      !window.confirm("Are you sure you want to send vouchers to the winners?")
    )
      return;

    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}api/manage-comps/${compId}/send_winner_voucher/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }
      );
      const data = await res.json();
      if (res.ok && data.status === "success") {
        setAlertMsg("Vouchers sent successfully!");
        fetchDetails();
      } else {
        setAlertMsg(data.message || "Error sending vouchers");
      }
    } catch (e) {
      console.error(e);
      setAlertMsg("Network Error");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    for (let i = 0; i < winners.length; i++) {
      if (winners[i].Status === 3 && !winners[i].Code) {
        setAlertMsg(`Please enter winner code for row ${i + 1}`);
        return;
      }
    }

    const updates = winners
      .filter((w) => w.Status !== w.OriginalStatus || w.Code !== w.OriginalCode)
      .map((w) => ({
        SchoolCode: w.SchoolCode,
        FamilyCode: w.FamilyCode,
        Login: w.Login,
        UniqueID: w.UniqueID,
        Status: w.Status,
        Code: w.Code,
        StatusLabel:
          statusOptions.find((o) => o.value === w.Status)?.label || "",
      }));

    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}api/manage-comps/${compId}/update_winners/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ updates }),
        }
      );
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    overlay: {
      position: "fixed" as const,
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      backgroundColor: "rgba(0,0,0,0.5)",
      zIndex: 9999,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    modal: {
      backgroundColor: "white",
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column" as const,
      border: "none",
      overflow: "hidden",
      fontFamily: "Arial, sans-serif",
      fontSize: "9pt",
    },
    popupHeader: {
      backgroundImage: `url(${popup_bg1})`,
      backgroundRepeat: "repeat-x",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      borderBottom: "1px solid #000",
      height: "30px",
      padding: "0 10px",
    },
    maroonHeader: {
      backgroundColor: "#800000",
      color: "white",
      padding: "5px",
      textAlign: "center" as const,
      fontWeight: "bold" as const,
      fontSize: "11pt",
      marginBottom: "5px",
    },
    wrapper: {
      width: "98%",
      margin: "0 auto",
      display: "flex",
      flexDirection: "column" as const,
      marginTop: "5px",
      flex: 1,
      overflow: "hidden",
      border: "1px solid #A0A0A0",
    },
    gridContainer: {
      flex: 1,
      overflow: "auto",
      backgroundColor: "white",
    },
    gridHeader: {
      backgroundColor: "#C7DDFD",
      fontWeight: "bold" as const,
      position: "sticky" as const,
      top: 0,
      borderBottom: "1px solid #A0A0A0",
    },
    th: {
      padding: "4px",
      borderRight: "1px solid #A0A0A0",
      textAlign: "left" as const,
      whiteSpace: "nowrap" as const,
    },
    td: {
      padding: "4px",
      borderRight: "1px solid #E0E0E0",
      borderBottom: "1px solid #E0E0E0",
    },
    footer: {
      padding: "10px",
      textAlign: "left" as const,
      borderTop: "1px solid #A0A0A0",
      backgroundColor: "#F0F0F0",
    },
    btn: {
      width: "100px",
      height: "25px",
      cursor: "pointer",
      marginRight: "10px",
    },
    input: {
      border: "1px solid #80A0FF",
      padding: "2px",
      fontFamily: "Arial",
      fontSize: "9pt",
    },
    disabledBtn: {
      opacity: 0.6,
      cursor: "not-allowed",
    },
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.popupHeader}>
          <span>&nbsp;</span>
          <img
            src={alert_close_n}
            alt="Close"
            onClick={onClose}
            style={{ cursor: "pointer" }}
            onMouseOver={(e) => (e.currentTarget.src = alert_close_o)}
            onMouseOut={(e) => (e.currentTarget.src = alert_close_n)}
          />
        </div>
        <div style={styles.wrapper}>
          <div style={styles.maroonHeader}>Competition: {compTitle}</div>
          <div style={styles.gridContainer}>
            {loading ? (
              <Loading />
            ) : (
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "9pt",
                }}
              >
                <thead style={styles.gridHeader}>
                  <tr>
                    <th style={styles.th}>#</th>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Grade/Year</th>
                    <th style={styles.th}>School/Edealer Code</th>
                    <th style={styles.th}>Address</th>
                    <th style={styles.th}>Country</th>
                    <th style={styles.th}>Email</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Code</th>
                    <th style={styles.th}>SchoolCode</th>
                    <th style={styles.th}>FamilyCode</th>
                    <th style={styles.th}>Login</th>
                  </tr>
                </thead>
                <tbody>
                  {winners.map((row, idx) => (
                    <tr
                      key={idx}
                      style={{
                        backgroundColor: idx % 2 === 0 ? "white" : "#F4FAFF",
                      }}
                    >
                      <td style={styles.td}>{idx + 1}</td>
                      <td style={styles.td}>{row.StudentName}</td>
                      <td style={styles.td}>{row.GradeYear}</td>
                      <td style={styles.td}>{row.SchoolName}</td>
                      <td style={styles.td}>{row.Address}</td>
                      <td style={styles.td}>{row.Country}</td>
                      <td style={styles.td}>{row.DisplayEmail}</td>
                      <td style={styles.td}>
                        <select
                          style={styles.input}
                          value={row.Status}
                          onChange={(e) =>
                            handleStatusChange(idx, parseInt(e.target.value))
                          }
                        >
                          <option value="0">-Select-</option>
                          {statusOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={styles.td}>
                        <input
                          style={{ ...styles.input, width: "100px" }}
                          value={row.Code || ""}
                          onChange={(e) =>
                            handleCodeChange(idx, e.target.value)
                          }
                          disabled={row.Status !== 3}
                        />
                      </td>
                      <td style={styles.td}>{row.SchoolCode}</td>
                      <td style={styles.td}>{row.FamilyCode}</td>
                      <td style={styles.td}>{row.Login}</td>
                    </tr>
                  ))}
                  {winners.length === 0 && (
                    <tr>
                      <td
                        colSpan={12}
                        style={{ padding: "20px", textAlign: "center" }}
                      >
                        No winners found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
          <div style={styles.footer}>
            <button onClick={onClose} style={styles.btn}>
              Close
            </button>
            <button onClick={handleSave} style={styles.btn}>
              Save
            </button>
            {canSendVouchers && (
              <button
                onClick={handleSendVouchers}
                disabled={!canSendVouchers}
                style={{
                  ...styles.btn,
                  width: "150px",
                  ...(!canSendVouchers ? styles.disabledBtn : {}),
                }}
              >
                Send Winner Voucher
              </button>
            )}
          </div>
        </div>
      </div>
      {alertMsg && (
          <CustomAlert 
              message={alertMsg} 
              onClose={() => setAlertMsg('')} 
          />
      )}
    </div>
  );
};

export default EL_CompWinnerDetails;